/**
 * Permission payload builder for the v2 endpoint:
 *
 *   POST /v2/users/composer/permission/{userId}
 *
 * Composes the four (and growing) filter buckets the backend accepts using
 * the generic DSL in `src/api/filters/compositeFilter.ts`. Callers pass a
 * single typed options object — anything they don't pass collapses to
 * `empty()` (which the server treats as "no constraint, return all").
 *
 * Keeping every per-category filter rule in one file means a server-side
 * contract change (new attribute name, new bucket) is a single-file edit.
 */

import {
  AnyFilter,
  CompositeFilter,
  and,
  anyOf,
  empty,
  isEmptyFilter,
} from '../filters/compositeFilter';

/**
 * What the caller can narrow the request by. Every field is optional —
 * omitting a field means "no filter for that attribute".
 */
export interface PermissionFilterOptions {
  /** Narrows `domainPermissions` + `domainAttributePermissions`. */
  domainIds?: ReadonlyArray<string>;
  /** Narrows `routePermissions`. */
  routeIds?: ReadonlyArray<string>;
  /** Narrows `operationPermissions`. */
  operationIds?: ReadonlyArray<string>;
  /** Narrows `workflowPermissions` and `stageSectionAttributePermissions`. */
  workflowIds?: ReadonlyArray<string>;
  stageIds?: ReadonlyArray<string>;
  sectionIds?: ReadonlyArray<string>;
}

/**
 * Server payload shape. Every bucket is a composite filter; new buckets
 * added on the backend slot in as additional optional fields.
 *
 * Note: `stageDomainAttributePermissions` is intentionally omitted — the
 * v2 backend does not accept it as a filter bucket. If it ever lands as
 * a real server contract, add it here in one place.
 */
export interface PermissionFilterPayload {
  domainPermissions: CompositeFilter;
  domainAttributePermissions: CompositeFilter;
  routePermissions: CompositeFilter;
  operationPermissions: CompositeFilter;
  workflowPermissions?: CompositeFilter;
  stageSectionAttributePermissions?: CompositeFilter;
}

/**
 * Internal helper: AND together only the non-empty filters. If every input
 * is empty the result is also empty — important because the backend
 * treats an empty composite as "no constraint", and we don't want a
 * `AND(empty, empty)` wrapper that could confuse downstream parsing.
 */
const andNonEmpty = (...filters: ReadonlyArray<AnyFilter>): CompositeFilter => {
  const populated = filters.filter((f) => !isEmptyFilter(f));
  if (populated.length === 0) return empty();
  if (populated.length === 1 && populated[0].type === 'compositeFilter') {
    return populated[0];
  }
  return and(...populated);
};

export const buildPermissionPayload = (
  options: PermissionFilterOptions = {},
): PermissionFilterPayload => {
  const domainFilter = anyOf('domainId', options.domainIds ?? []);
  const workflowFilter = anyOf('workflowId', options.workflowIds ?? []);
  const stageFilter = anyOf('stageId', options.stageIds ?? []);
  const sectionFilter = anyOf('sectionId', options.sectionIds ?? []);

  const stageScoped = andNonEmpty(workflowFilter, stageFilter, sectionFilter);

  return {
    domainPermissions: domainFilter,
    domainAttributePermissions: domainFilter,
    routePermissions: anyOf('routeId', options.routeIds ?? []),
    operationPermissions: anyOf('operationId', options.operationIds ?? []),
    workflowPermissions: workflowFilter,
    stageSectionAttributePermissions: stageScoped,
  };
};
